import React, { useState } from 'react';
import { 
  Box, Typography, Button, TextField, Stepper, Step, StepLabel, 
  IconButton, InputAdornment, DialogContent, Zoom, Dialog, 
  Snackbar, Alert 
} from '@mui/material';
import { FaArrowRight, FaCheckCircle, FaLink } from 'react-icons/fa';
import { styled, keyframes } from '@mui/material/styles';
import Cookies from 'js-cookie';

const backgroundMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

export const AnimatedDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(10px)',
    backgroundColor: darkMode ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.4)',
  },
  '& .MuiPaper-root': {
    borderRadius: '24px',
    border: `2px solid ${darkMode ? '#ff4d6d' : '#ff758f'}`,
    background: darkMode 
        ? 'linear-gradient(45deg, #000000 10%, #4a0512 50%, #000000 90%)' 
        : 'linear-gradient(45deg, #fff0f3 30%, #ffccd5 90%)',
    backgroundSize: '200% 200%',
    animation: `${backgroundMove} 12s ease infinite`,
    color: darkMode ? 'white' : '#444',
    boxShadow: darkMode ? '0 0 30px rgba(255, 77, 109, 0.5)' : '0 10px 30px rgba(255, 121, 143, 0.3)',
    overflow: 'hidden',
  },
}));

interface CreatorProps {
    open: boolean;
    onClose: () => void;
    darkMode: boolean;
}

const ValentinCardCreator: React.FC<CreatorProps> = ({ open, onClose, darkMode }) => {
  const [activeStep, setActiveStep] = useState(0); 
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    p1: { q: '', yes: '', no: '' },
    p2: { q: '', yes: '', no: '' },
    p3: { q: '', yes: '', no: '' },
    p4: { lastTitle: '' }
  });

  const steps = ['Phase 1', 'Phase 2', 'Phase 3', 'Final Message'];

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleCreate = async () => {
    const payload = {
      phase1Qt: formData.p1.q,
      phase1Ybtn: formData.p1.yes,
      phase1Nbtn: formData.p1.no,
      phase2Qt: formData.p2.q,
      phase2Ybtn: formData.p2.yes,
      phase2Nbtn: formData.p2.no,
      phase3Qt: formData.p3.q,
      phase3Ybtn: formData.p3.yes,
      phase3Nbtn: formData.p3.no,
      finalSuccessMessage: formData.p4.lastTitle
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/user/valentine/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Cookies.get('token')}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to save card');

      const result = await response.json(); 

      const finalUrl = `${window.location.origin}/valentine-preview?id=${result.url}`;
      
      setGeneratedUrl(finalUrl);
      setActiveStep(4); 
    } catch (error) {
      console.error("Hiba a mentés során:", error);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedUrl);
    setSnackbarOpen(true);
  };

  const handleOpenLink = () => {
    if (generatedUrl) {
      window.open(generatedUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
      color: darkMode ? 'white' : '#444',
      '& fieldset': { borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)' },
      '&:hover fieldset': { borderColor: '#ff4d6d' },
    },
    '& .MuiInputLabel-root': { color: darkMode ? '#aaa' : '#666' },
    mb: 2
  };

  // Segéd stílus a karakter számlálónak
  const helperTextStyle = { 
    sx: { 
      textAlign: 'right', 
      color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
      marginTop: '-12px',
      marginBottom: '8px'
    } 
  };

  const renderStepContent = (stepIdx: number) => {
    if (stepIdx < 3) {
      const pKey = `p${stepIdx + 1}` as keyof typeof formData;
      const data = formData[pKey] as { q: string, yes: string, no: string };
      
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
          <Typography variant="subtitle2" sx={{ color: '#ff4d6d', fontWeight: 'bold' }}>Phase {stepIdx + 1} Settings</Typography>
          
          <TextField 
            label="Question Text" 
            fullWidth 
            variant="outlined"
            sx={textFieldStyle}
            value={data.q}
            onChange={(e) => setFormData({...formData, [pKey]: {...data, q: e.target.value}})}
            inputProps={{ maxLength: 32 }}
            helperText={`${data.q.length}/32`}
            FormHelperTextProps={helperTextStyle}
          />
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField 
              label="Yes Button" 
              fullWidth 
              sx={textFieldStyle} 
              value={data.yes} 
              onChange={(e) => setFormData({...formData, [pKey]: {...data, yes: e.target.value}})} 
              inputProps={{ maxLength: 32 }}
              helperText={`${data.yes.length}/32`}
              FormHelperTextProps={helperTextStyle}
            />
            <TextField 
              label="No Button" 
              fullWidth 
              sx={textFieldStyle} 
              value={data.no} 
              onChange={(e) => setFormData({...formData, [pKey]: {...data, no: e.target.value}})} 
              inputProps={{ maxLength: 32 }}
              helperText={`${data.no.length}/32`}
              FormHelperTextProps={helperTextStyle}
            />
          </Box>
        </Box>
      );
    }
    return (
      <Box sx={{ mt: 2 }}>
        <TextField 
          label="Final Success Message" 
          fullWidth multiline rows={3} 
          sx={textFieldStyle}
          value={formData.p4.lastTitle} 
          onChange={(e) => setFormData({...formData, p4: {lastTitle: e.target.value}})} 
          inputProps={{ maxLength: 32 }}
          helperText={`${formData.p4.lastTitle.length}/32`}
          FormHelperTextProps={{ sx: { textAlign: 'right', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' } }}
        />
      </Box>
    );
  };

  return (
    <>
      <AnimatedDialog open={open} onClose={onClose} darkMode={darkMode} maxWidth="sm" fullWidth TransitionComponent={Zoom}>
        <DialogContent sx={{ minHeight: '400px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {activeStep < 4 ? (
            <>
              <Stepper 
                  activeStep={activeStep} 
                  alternativeLabel 
                  sx={{ 
                      mb: 2,
                      '& .MuiStepLabel-label': { color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' },
                      '& .MuiStepIcon-root': { color: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)' },
                      '& .MuiStepIcon-root.Mui-active': { color: '#ff4d6d' },
                      '& .MuiStepIcon-root.Mui-completed': { color: '#ff758f' },
                  }}
              >
                {steps.map((label) => (
                  <Step key={label}><StepLabel>{label}</StepLabel></Step>
                ))}
              </Stepper>

              {renderStepContent(activeStep)}

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={activeStep === 0 ? onClose : handleBack} sx={{ color: darkMode ? 'white' : '#444' }}>
                  {activeStep === 0 ? "Cancel" : "Back"}
                </Button>
                {activeStep === 3 ? (
                  <Button variant="contained" color="success" onClick={handleCreate} startIcon={<FaCheckCircle />}>Finish</Button>
                ) : (
                  <Button variant="contained" onClick={handleNext} endIcon={<FaArrowRight />} sx={{ bgcolor: '#ff4d6d', '&:hover': { bgcolor: '#ff758f' } }}>Next</Button>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <FaCheckCircle size={60} color="#4caf50" />
              <Typography variant="h5" sx={{ my: 2, fontWeight: 'bold', color: darkMode ? 'white' : '#444' }}>Your Card is Ready!</Typography>
              <TextField
                fullWidth
                variant="filled"
                value={generatedUrl}
                onClick={handleOpenLink}
                placeholder="Click to preview..."
                sx={{ 
                  mt: 2, 
                  bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', 
                  borderRadius: '10px',
                  cursor: 'pointer',
                  '& .MuiFilledInput-root': { cursor: 'pointer' },
                  input: { color: darkMode ? '#ffccd5' : '#ff4d6d', cursor: 'pointer', textDecoration: 'underline' }
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={(e) => { e.stopPropagation(); handleCopyLink(); }} color="primary">
                        <FaLink />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="caption" sx={{ color: darkMode ? '#aaa' : '#888', mt: 1, display: 'block' }}>
                Click the URL to preview or the icon to copy!
              </Typography>
              <Button sx={{ mt: 4, color: darkMode ? 'white' : '#444' }} onClick={onClose}>Close</Button>
            </Box>
          )}
        </DialogContent>
      </AnimatedDialog>

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={3000} 
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" sx={{ width: '100%', borderRadius: '12px' }}>
          Link copied to clipboard! 💖
        </Alert>
      </Snackbar>
    </>
  );
};

export default ValentinCardCreator;